import { useEffect } from "react";
import { MESSAGE_TYPES } from "../../stores/websocketStore";
import { toast } from "../../universal/toast";
import { bumpLobbySettingsResetVersion } from "../../stores/lobbySettingsPanelStore";
import {
    handleLobbyPlayerAddedMessage,
    handleLobbyPlayerLeftMessage,
} from "./lobbyRealtimeMessageHandlers";

export default function useLobbyRealtimeRouter({
    actions,
    catalog,
    friendsRef,
    handleGenerateInviteCode,
    history,
    resolvedUserSteamId,
    setInviteCode,
    setInviteExpiresAt,
    subscribe,
    user,
}) {
    useEffect(() => {
        const unsubscribe = subscribe(({ message }) => {
            if (!message) return;

            const eventAsJson = message;
            if (process.env.NODE_ENV !== "production") console.log("Received WebSocket message:", eventAsJson);

            switch (eventAsJson.messageId) {
                case MESSAGE_TYPES.FRIEND_REQUEST_ACCEPTED: {
                    if (process.env.NODE_ENV !== "production") console.log("friend request accepted", eventAsJson);

                    const newFriend = {
                        userA: eventAsJson.userA,
                        userB: eventAsJson.userB,
                        friendSteamId: eventAsJson.friendSteamId,
                        isUserA: eventAsJson.userA === resolvedUserSteamId,
                        friendData: {
                            _id: eventAsJson.friendData._id,
                            userId: eventAsJson.friendData.userId,
                            picture: eventAsJson.friendData.picture,
                            elo: eventAsJson.friendData.elo,
                            nickname: eventAsJson.friendData.nickname,
                            isOnline: eventAsJson.friendData.isOnline,
                        },
                    };

                    actions.friendRequestAccepted({ newFriend });
                    break;
                }

                case MESSAGE_TYPES.FRIEND_STATUS_UPDATE:
                    if (process.env.NODE_ENV !== "production") console.log("Updating friend status:", eventAsJson);
                    actions.updateFriendStatus({
                        friendSteamId: eventAsJson.friendSteamId,
                        isOnline: eventAsJson.isOnline,
                        playerStatus: eventAsJson.playerStatus,
                        lastOnlineUpdate: eventAsJson.lastOnlineUpdate,
                    });
                    break;

                case MESSAGE_TYPES.LOBBY_MEMBER_STATUS_UPDATE:
                    if (process.env.NODE_ENV !== "production") console.log("Updating lobby member status:", eventAsJson);
                    actions.updateLobbyMemberStatus({
                        userId: eventAsJson.userId,
                        isOnline: eventAsJson.isOnline,
                        playerStatus: eventAsJson.playerStatus,
                        lastOnlineUpdate: eventAsJson.lastOnlineUpdate,
                    });
                    break;

                case MESSAGE_TYPES.LOBBY_CREATED:
                    actions.createLobby(eventAsJson.lobby);
                    actions.closeJoinLobbyDialog();
                    toast.success("Lobby created", { durationMs: 4000 });

                    if (eventAsJson.inviteCode) {
                        setInviteCode(eventAsJson.inviteCode);
                        setInviteExpiresAt(eventAsJson.inviteCodeExpiresAt);
                    } else if (eventAsJson.lobby?.lobbyCode) {
                        handleGenerateInviteCode();
                    }
                    break;

                case MESSAGE_TYPES.LOBBY_PLAYER_LEFT:
                    handleLobbyPlayerLeftMessage({
                        event: eventAsJson,
                        user,
                        actions,
                        friends: friendsRef.current || [],
                        clearInviteCodeState() {
                            setInviteCode(null);
                            setInviteExpiresAt(null);
                        },
                    });
                    break;

                case MESSAGE_TYPES.LOBBY_PLAYER_REMOVED_SILENT:
                    actions.lobbyPlayerLeft(
                        eventAsJson.lobbyCode,
                        eventAsJson.removedUserId,
                        false,
                        null,
                        eventAsJson.totalPlayers,
                    );
                    break;

                case MESSAGE_TYPES.LOBBY_ADDED_PLAYER:
                    handleLobbyPlayerAddedMessage({
                        event: eventAsJson,
                        user,
                        actions,
                        currentLobby: catalog.currentLobby,
                        friends: friendsRef.current || [],
                    });
                    break;

                case MESSAGE_TYPES.LOBBY_OPTIONS_UPDATED:
                    actions.lobbyLeaderUpdatedOptions({
                        lobbyCode: eventAsJson.lobbyCode,
                        lobbyOptions: eventAsJson.lobbyOptions,
                    });
                    break;

                case MESSAGE_TYPES.LOBBY_MAPS_UPDATED:
                    actions.lobbyLeaderUpdatedMaps({
                        lobbyCode: eventAsJson.lobbyCode,
                        lobbyMapsSelected: eventAsJson.lobbyMapsSelected,
                        lobbyWingmanMapsSelected: eventAsJson.lobbyWingmanMapsSelected,
                    });
                    break;

                case MESSAGE_TYPES.LOBBY_QUEUE_MODE_UPDATED:
                    actions.lobbyLeaderUpdatedQueueMode({
                        lobbyCode: eventAsJson.lobbyCode,
                        queueMode: eventAsJson.queueMode,
                        maxPlayers: eventAsJson.maxPlayers,
                        lobbyWingmanMapsSelected: eventAsJson.lobbyWingmanMapsSelected,
                    });
                    break;

                case MESSAGE_TYPES.LOBBY_SERVER_LOCATIONS_UPDATED:
                    actions.lobbyLeaderUpdatedServerLocations({
                        lobbyCode: eventAsJson.lobbyCode,
                        lobbyServerLocationsSelected:
                            eventAsJson.lobbyServerLocationsSelected,
                    });
                    break;

                case MESSAGE_TYPES.LOBBY_SETTINGS_DENIED:
                    bumpLobbySettingsResetVersion();
                    actions.showQueueErrorMessage(
                        eventAsJson.message
                            || (eventAsJson.reason === "tier-locked"
                                ? "Your current plan cannot edit these shared lobby settings."
                                : eventAsJson.reason === "invalid-selection"
                                    ? "That lobby setting change is not valid."
                                    : eventAsJson.reason === "searching"
                                        ? "Stop searching before switching queue mode."
                                        : "Only the lobby leader can change settings."),
                    );
                    break;

                case MESSAGE_TYPES.LOBBY_PLAYER_KICKED: {
                    const message = eventAsJson.messageKick;
                    const kickedId = eventAsJson.kickedPlayerId;
                    if (user && kickedId === user.userId) {
                        actions.leaveLobby();
                        actions.notifyPlayerHasBeenKicked(message);
                        setInviteCode(null);
                        setInviteExpiresAt(null);
                    } else if (kickedId) {
                        actions.lobbyPlayerLeft(eventAsJson.lobbyCode, kickedId);
                        actions.notifyPlayerHasBeenKicked(message);
                    }
                    break;
                }

                case MESSAGE_TYPES.FRIEND_REQUEST_RECEIVED:
                    if (process.env.NODE_ENV !== "production") console.log("Friend request received via WebSocket:", eventAsJson);
                    actions.addFriendRequestLocally(
                        eventAsJson.fromUserSteamId,
                        eventAsJson.toUserSteamId,
                    );
                    actions.initFriendRequests(resolvedUserSteamId);
                    break;

                case MESSAGE_TYPES.FRIEND_REQUEST_ACCEPTED_NOTIFICATION:
                    if (process.env.NODE_ENV !== "production") console.log("Friend request accepted notification:", eventAsJson);
                    if (eventAsJson.friendSteamId) {
                        actions.removePendingRequestLocally(eventAsJson.friendSteamId);
                    }
                    actions.getFriends({
                        userId: user.userId,
                        steamId: resolvedUserSteamId,
                    });
                    actions.getPendingRequests(resolvedUserSteamId);

                    if (catalog.currentLobby) {
                        actions.getCurrentLobby(catalog.currentLobby.lobbyCode);
                    }
                    break;

                case MESSAGE_TYPES.FRIEND_REQUEST_DECLINED_NOTIFICATION:
                    if (process.env.NODE_ENV !== "production") console.log("Friend request declined notification:", eventAsJson);
                    if (eventAsJson.toUserSteamId) {
                        actions.removePendingRequestLocally(eventAsJson.toUserSteamId);
                    }
                    actions.getPendingRequests(resolvedUserSteamId);
                    break;

                case MESSAGE_TYPES.FRIEND_DELETED_NOTIFICATION:
                    if (eventAsJson.deletedBySteamId) {
                        actions.removeFriendLocally(eventAsJson.deletedBySteamId);
                        actions.removePendingRequestLocally(eventAsJson.deletedBySteamId);
                    }
                    break;

                case MESSAGE_TYPES.FRIENDS_BOOTSTRAP:
                    actions.setFriendsFromBootstrap(eventAsJson.friends);
                    break;

                case MESSAGE_TYPES.FRIEND_REQUESTS_BOOTSTRAP:
                    actions.setFriendRequestsFromBootstrap(eventAsJson.friendRequests);
                    break;

                case MESSAGE_TYPES.FRIENDS_LIST:
                    actions.setFriendsFromBootstrap(eventAsJson.friends);
                    break;

                case MESSAGE_TYPES.FRIEND_REQUEST_ERROR:
                    if (process.env.NODE_ENV !== "production") console.log("Friend request error via WebSocket:", eventAsJson);
                    actions.sendFriendRequestError(eventAsJson.error);

                    if (
                        eventAsJson.toUserSteamId &&
                        !eventAsJson.error.includes("already exists")
                    ) {
                        actions.removePendingRequestLocally(eventAsJson.toUserSteamId);
                    }
                    break;

                case MESSAGE_TYPES.LOBBY_CODE_NOT_FOUND:
                    actions.lobbyCodeNotFound();
                    break;

                case MESSAGE_TYPES.LOBBY_FULL:
                    actions.lobbyFull();
                    break;

                case MESSAGE_TYPES.JOINED_LOBBY_BY_CODE:
                    actions.closeJoinLobbyDialog();
                    break;

                case MESSAGE_TYPES.SOLO_LOBBY_ERROR:
                    console.error("Error creating solo lobby:", eventAsJson.error);
                    actions.showUnavailableActionMessage();
                    break;

                case MESSAGE_TYPES.INIT_HANDSHAKE:
                case MESSAGE_TYPES.HANDSHAKE_COMPLETED:
                    break;

                case MESSAGE_TYPES.PLATFORM_STATS_UPDATE:
                    actions.setPlatformStats({
                        serversOnline: eventAsJson.serversOnline,
                        playersSearching: eventAsJson.playersSearching,
                        activeMatches: eventAsJson.activeMatches,
                    });
                    break;

                default:
                    if (process.env.NODE_ENV !== "production") console.log("Unhandled message type:", eventAsJson.messageId);
            }
        });

        return unsubscribe;
    }, [
        actions,
        catalog,
        friendsRef,
        handleGenerateInviteCode,
        history,
        resolvedUserSteamId,
        setInviteCode,
        setInviteExpiresAt,
        subscribe,
        user,
    ]);
}
